import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  GuestHeader,
  WelcomeCard,
  HouseRulesCard,
  AmenitiesCard,
  HostContactCard,
  SharedAmenitiesCard,
  GuestExploreTab,
  HostCornerTab,
  FacilitiesSection,
  GuestEquipmentSection,
} from '@/components/guest';
import { UnitSwitcher } from '@/components/guest/UnitSwitcher';
import { SleepingCard } from '@/components/guest/SleepingCard';
import { SleepingSection } from '@/components/guest/SleepingSection';
import { DemoBanner } from '@/components/guest/DemoBanner';
import { DemoChatScreen } from '@/components/guest/demo/DemoChatScreen';
import { DemoFloatingAssistant } from '@/components/guest/demo/DemoFloatingAssistant';
import {
  DemoQuickActionsGrid,
  DemoWeatherBanner,
} from '@/components/guest/demo/DemoQuickActionsGrid';
import {
  DemoExplainTooltip,
  type ExplainContext,
} from '@/components/guest/demo/DemoExplainTooltip';
import type { GuestTab } from '@/components/guest';
import {
  DEMO_PROPERTY,
  DEMO_LANDLORD,
  DEMO_UNITS,
  DEMO_SLEEPING_UNIT1,
  DEMO_SLEEPING_UNIT2,
  DEMO_GROUP,
  DEMO_UNIT_BOOKINGS,
  DEMO_HOUSE_RULES,
  DEMO_CHECK_TIMES,
  DEMO_SHARED_AMENITIES,
  DEMO_CHAT_MESSAGES,
  DEMO_RECOMMENDATIONS,
  DEMO_PERSONAL_RECOMMENDATIONS,
  DEMO_SERVICES,
  DEMO_IMAGES,
  DEMO_FACILITIES,
  DEMO_EQUIPMENT,
  DEMO_HOST_UPDATES,
  getLocalizedText,
} from '@/data/demoMockData';
import {
  Car, MapPin, X,
  ClipboardList, BedDouble, Bath, Users,
  Maximize, Moon, Sun, Home, Droplets,
  Bed, Compass, Heart, Info, ChevronLeft, ChevronRight, ZoomIn, Images as ImagesIcon, Camera, ChefHat, Umbrella,
} from 'lucide-react';
import { formatCurrency } from '@/lib/currency';

// ============================================================
// (DemoWeatherWidget removed — now handled by DemoQuickActionsGrid + DemoWeatherBanner)
// ============================================================

// ============================================================
// INLINE: DemoCategoryGallery (gallery with category tabs + fullscreen lightbox via portal)
// ============================================================
import { DEMO_CATEGORIZED_IMAGES } from '@/data/demoMockData';

const CATEGORY_CONFIG: Record<string, { icon: typeof Home; label: { hr: string; en: string }; color: string }> = {
  all:            { icon: ImagesIcon, label: { hr: 'Sve', en: 'All' }, color: 'text-cyan-500' },
  accommodation:  { icon: Home,       label: { hr: 'Sobe', en: 'Rooms' }, color: 'text-blue-500' },
  kitchen:        { icon: ChefHat,    label: { hr: 'Kuhinja', en: 'Kitchen' }, color: 'text-orange-500' },
  bathroom:       { icon: Bath,       label: { hr: 'Kupaonica', en: 'Bathroom' }, color: 'text-sky-500' },
  terrace:        { icon: Sun,        label: { hr: 'Terasa', en: 'Terrace' }, color: 'text-amber-500' },
  pool:           { icon: Droplets,   label: { hr: 'Bazen', en: 'Pool' }, color: 'text-cyan-500' },
  exterior:       { icon: Home,       label: { hr: 'Eksterijer', en: 'Exterior' }, color: 'text-slate-500' },
  parking:        { icon: Car,        label: { hr: 'Parking', en: 'Parking' }, color: 'text-gray-500' },
  beach:          { icon: Umbrella,   label: { hr: 'Plaža', en: 'Beach' }, color: 'text-yellow-500' },
  surroundings:   { icon: MapPin,     label: { hr: 'Okolica', en: 'Surroundings' }, color: 'text-teal-500' },
};

function DemoCategoryGallery({ unitKey, darkMode, lang }: { unitKey: 'unit1' | 'unit2'; darkMode: boolean; lang: 'hr' | 'en' }) {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  // Combine unit + property images
  const allImages = useMemo(() => [
    ...DEMO_CATEGORIZED_IMAGES[unitKey],
    ...DEMO_CATEGORIZED_IMAGES.property,
  ], [unitKey]);

  // Get unique categories that have images
  const categories = useMemo(() => {
    const cats = [...new Set(allImages.map(img => img.category))];
    return ['all', ...cats];
  }, [allImages]);

  // Filter by active category
  const filteredImages = useMemo(() =>
    activeCategory === 'all' ? allImages : allImages.filter(img => img.category === activeCategory),
    [allImages, activeCategory]
  );

  const closeLightbox = useCallback(() => setSelectedIndex(null), []);
  const goNext = useCallback(() => {
    if (selectedIndex !== null) setSelectedIndex((selectedIndex + 1) % filteredImages.length);
  }, [selectedIndex, filteredImages.length]);
  const goPrev = useCallback(() => {
    if (selectedIndex !== null) setSelectedIndex((selectedIndex - 1 + filteredImages.length) % filteredImages.length);
  }, [selectedIndex, filteredImages.length]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIndex, closeLightbox, goNext, goPrev]);

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev(); }
    setTouchStart(null);
  };

  return (
    <>
      <div className={`rounded-2xl overflow-hidden ${
        darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-100 shadow-xl'
      }`}>
        {/* Header */}
        <div className="p-4 flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${
            darkMode ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gradient-to-br from-cyan-400 to-blue-500'
          } shadow-lg shadow-cyan-500/30`}>
            <ImagesIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.gallery.title', 'Galerija')}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {filteredImages.length} {t('guest.gallery.photos', 'fotografija')}
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div className="px-4 pb-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => {
              const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.all;
              const CatIcon = config.icon;
              const isActive = activeCategory === cat;
              const count = cat === 'all' ? allImages.length : allImages.filter(img => img.category === cat).length;
              return (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive
                      ? darkMode ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                      : darkMode ? 'bg-slate-700/50 text-slate-400 border border-slate-600/50' : 'bg-gray-100 text-gray-500 border border-gray-200'
                  }`}>
                  <CatIcon className={`w-3.5 h-3.5 ${isActive ? config.color : ''}`} />
                  <span>{config.label[lang]}</span>
                  <span className={`text-[10px] ${isActive ? 'opacity-80' : 'opacity-50'}`}>({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Image grid */}
        <div className="px-4 pb-4">
          <div className="grid grid-cols-3 gap-1.5">
            {filteredImages.map((img, i) => (
              <button key={`${img.url}-${i}`} onClick={() => setSelectedIndex(i)}
                className="relative aspect-square rounded-lg overflow-hidden group">
                <img src={img.url} alt={img.label[lang]} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                  <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {/* Category label on hover */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-white text-[10px] font-medium">{img.label[lang]}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Fullscreen lightbox via portal */}
      {selectedIndex !== null && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <button onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
            <span className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white font-medium">
              {selectedIndex + 1} / {filteredImages.length}
            </span>
            {filteredImages[selectedIndex] && (
              <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white/70 text-sm">
                {filteredImages[selectedIndex].label[lang]}
              </span>
            )}
          </div>
          {filteredImages.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10">
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10">
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}
          <img src={filteredImages[selectedIndex].url} alt={filteredImages[selectedIndex].label[lang]}
            className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          {filteredImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 rounded-xl bg-black/50 backdrop-blur-sm max-w-[90vw] overflow-x-auto">
              {filteredImages.map((img, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setSelectedIndex(i); }}
                  className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                    i === selectedIndex ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
                  }`}>
                  <img src={img.url} alt={img.label[lang]} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

// ============================================================
// INLINE: DemoHostMoments (replaces HostUpdatesSection - lightbox via portal)
// ============================================================
function DemoHostMoments({ updates, darkMode, portalTarget }: { updates: typeof import('@/data/demoMockData').DEMO_HOST_UPDATES; darkMode: boolean; portalTarget?: HTMLElement | null }) {
  const { i18n } = useTranslation();
  const isHr = i18n.language === 'hr';
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const getTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return isHr ? `prije ${days}d` : `${days}d ago`;
    if (hours > 0) return isHr ? `prije ${hours}h` : `${hours}h ago`;
    if (minutes > 0) return isHr ? `prije ${minutes}min` : `${minutes}m ago`;
    return isHr ? 'upravo' : 'just now';
  };

  if (!updates || updates.length === 0) return null;

  return (
    <>
      <div className={`rounded-2xl p-4 ${
        darkMode
          ? 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20'
          : 'bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-100'
      }`}>
        <div className="flex items-center gap-2.5 mb-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
            <Camera className={`w-4 h-4 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
          </div>
          <div>
            <h3 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {isHr ? 'Trenutci' : 'Moments'}
            </h3>
            <p className={`text-[11px] ${darkMode ? 'text-cyan-400/70' : 'text-cyan-600/70'}`}>
              {isHr ? 'Novosti od domaćina' : 'News from your host'}
            </p>
          </div>
          {updates.length > 1 && (
            <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
              darkMode ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
            }`}>
              {updates.length} {isHr ? 'objava' : 'posts'}
            </span>
          )}
        </div>

        <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide">
          {updates.map((update, index) => (
            <button key={update.id} onClick={() => setLightboxIndex(index)}
              className="flex-shrink-0 flex flex-col items-center gap-1.5 snap-start group">
              <div className={`p-[2.5px] rounded-full bg-gradient-to-br ${
                darkMode ? 'from-cyan-400 via-blue-500 to-cyan-500' : 'from-cyan-500 via-blue-500 to-cyan-500'
              }`}>
                <div className={`w-16 h-16 rounded-full overflow-hidden border-2 ${darkMode ? 'border-slate-900' : 'border-white'}`}>
                  <img src={update.image_url} alt={update.caption || ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" loading="lazy" />
                </div>
              </div>
              <span className={`text-[10px] font-medium max-w-[72px] truncate ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {getTimeAgo(update.created_at)}
              </span>
            </button>
          ))}
        </div>

        {updates[0]?.caption && (
          <p className={`mt-2.5 text-xs italic line-clamp-1 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            &ldquo;{updates[0].caption}&rdquo;
          </p>
        )}
      </div>

      {/* Lightbox - portaled to phone frame so it stays inside */}
      {lightboxIndex !== null && portalTarget && createPortal(
        <div className="absolute inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={() => setLightboxIndex(null)}>
          <button onClick={() => setLightboxIndex(null)}
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm">
            {lightboxIndex + 1} / {updates.length}
          </div>
          {updates.length > 1 && (
            <>
              {lightboxIndex > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
                  className="absolute left-2 z-10 w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20">
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {lightboxIndex < updates.length - 1 && (
                <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
                  className="absolute right-2 z-10 w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20">
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </>
          )}
          <div className="max-w-full max-h-[80%] flex flex-col items-center px-3 mt-8" onClick={(e) => e.stopPropagation()}>
            <img src={updates[lightboxIndex].image_url} alt={updates[lightboxIndex].caption || ''}
              className="max-w-full max-h-[60%] object-contain rounded-lg" />
            {updates[lightboxIndex].caption && (
              <p className="mt-3 text-white/80 text-xs text-center max-w-[95%] px-2 leading-relaxed">{updates[lightboxIndex].caption}</p>
            )}
            <p className="mt-1 text-white/40 text-[10px]">{getTimeAgo(updates[lightboxIndex].created_at)}</p>
          </div>
        </div>,
        portalTarget
      )}
    </>
  );
}

// (Inline DemoQuickActions / DemoPaymentSection / DemoDocuments removed — replaced by DemoQuickActionsGrid)


// ============================================================
// MAIN: DemoGuestPortalPage
// ============================================================
export default function DemoGuestPortalPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split('-')[0] || 'hr') as 'hr' | 'en';

  const [activeTab, setActiveTab] = useState<GuestTab>('home');
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('guest-dark-mode');
      if (saved !== null) return saved === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });
  const [showChat, setShowChat] = useState(false);
  const [showChatTyping] = useState(false);
  const [activeUnitId, setActiveUnitId] = useState<string>(DEMO_UNITS.unit1.id);

  // ========== Contextual Tooltip State ==========
  // Shows on first visit / first tab change to explain what landlord is looking at
  const [explainContext, setExplainContext] = useState<ExplainContext | null>('home');
  const [activeModal, setActiveModal] = useState<
    'wifi' | 'notifications' | 'navigation' | 'payment' | 'arrival' | 'onlineCheckin' | 'share' | 'weather' | null
  >(null);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('guest-dark-mode', String(darkMode));
  }, [darkMode]);

  // Handle chat tab (legacy: clicking 'chat' in nav opens chat overlay)
  useEffect(() => {
    if (activeTab === 'chat') {
      setShowChat(true);
      setActiveTab('home');
    }
  }, [activeTab]);

  // ========== Tab change → show contextual tooltip ==========
  useEffect(() => {
    // Don't override modal/chat tooltip
    if (activeModal || showChat) return;

    const map: Record<GuestTab, ExplainContext | null> = {
      home: 'home',
      unit: 'unit',
      explore: 'explore',
      host: 'host',
      info: 'info',
      chat: null,
    };
    setExplainContext(map[activeTab]);
  }, [activeTab, activeModal, showChat]);

  // ========== Modal opens → show modal-specific tooltip ==========
  useEffect(() => {
    if (!activeModal) return;
    const map: Record<NonNullable<typeof activeModal>, ExplainContext | null> = {
      wifi: 'modal-wifi',
      notifications: 'modal-notifications',
      navigation: 'modal-navigation',
      payment: 'modal-payment',
      arrival: 'modal-arrival',
      share: 'modal-share',
      onlineCheckin: null, // No specific tooltip for these
      weather: null,
    };
    setExplainContext(map[activeModal]);
  }, [activeModal]);

  // ========== Chat opens → show chat tooltip ==========
  useEffect(() => {
    if (showChat) {
      setExplainContext('chat');
    }
  }, [showChat]);

  // Active unit data
  const isUnit1 = activeUnitId === DEMO_UNITS.unit1.id;
  const activeUnit = isUnit1 ? DEMO_UNITS.unit1 : DEMO_UNITS.unit2;
  const activeSleeping = isUnit1 ? DEMO_SLEEPING_UNIT1 : DEMO_SLEEPING_UNIT2;
  const activeBooking = DEMO_GROUP.bookings.find(b => b.unitId === activeUnitId) || DEMO_GROUP.bookings[0];

  // Combine unit + property images for gallery
  // Non-personal recommendations for Explore tab
  const exploreRecommendations = useMemo(
    () => DEMO_RECOMMENDATIONS.filter(r => !r.is_personal_recommendation),
    []
  );

  const phoneFrameRef = useRef<HTMLDivElement>(null);

  // Detect desktop for phone frame (md = 768px)
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <>
      {/* Not prerendered (see vite.config.ts), so the raw HTML is the
          homepage's — keep the sandbox out of the index rather than let
          Google file it as a second homepage. */}
      <Helmet>
        <title>{lang === 'hr' ? 'Demo aplikacije za goste | BookiApp' : 'Guest app demo | BookiApp'}</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <style>{`
        @keyframes wave {
          0%, 100% { transform: translateX(0) translateY(0); }
          50% { transform: translateX(-25px) translateY(5px); }
        }
      `}</style>

      <div style={isDesktop ? {
        minHeight: '100vh',
        background: '#030712',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '2rem',
        paddingBottom: '2rem',
      } : { minHeight: '100vh' }}>
        {/* Demo Banner */}
        <div style={isDesktop ? { width: '100%', maxWidth: 430 } : {}}>
          <DemoBanner />
        </div>

        {/* Phone frame on desktop (430px, rounded, shadow) */}
        {/* Full screen on mobile */}
        {/* Phone frame container with rounded corners */}
        <div
          ref={phoneFrameRef}
          className={`flex flex-col transition-colors duration-500 ${
            darkMode
              ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
              : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
          }`}
          style={isDesktop ? {
            position: 'relative' as const,
            width: 430,
            height: '88vh',
            borderRadius: '1.5rem',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          } : {
            position: 'relative' as const,
            width: '100%',
            height: '100dvh',
            overflow: 'hidden',
          }}
        >

      {/* Scrollable area (header + content) */}
      <div data-demo-scroll className="flex-1 min-h-0 overflow-y-auto relative">

      {/* Summer Seasonal Decorations (waves + sun) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20">
          <svg viewBox="0 0 1440 320" className="absolute bottom-0 w-full animate-[wave_8s_ease-in-out_infinite]">
            <path
              fill={darkMode ? '#0891b2' : '#06b6d4'}
              d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,165.3C672,171,768,213,864,218.7C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </svg>
          <svg viewBox="0 0 1440 320" className="absolute bottom-0 w-full animate-[wave_10s_ease-in-out_infinite_0.5s]">
            <path
              fill={darkMode ? '#0e7490' : '#22d3ee'}
              fillOpacity="0.5"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,234.7C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </svg>
        </div>
        {/* Sun decoration */}
        <div className="absolute top-20 right-4 w-16 h-16 bg-yellow-300/20 rounded-full blur-xl animate-pulse" />
      </div>

      {/* Header */}
      <GuestHeader
        propertyName={DEMO_PROPERTY.name}
        unitName={getLocalizedText(activeUnit.name, lang)}
        checkIn={DEMO_GROUP.checkIn}
        checkOut={DEMO_GROUP.checkOut}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        onChatClick={() => setShowChat(true)}
        unreadMessages={0}
        landlordName={`${DEMO_LANDLORD.firstName} ${DEMO_LANDLORD.lastName}`}
        chatEnabled={true}
      />

      {/* Main Content */}
      <main className="max-w-lg mx-auto relative z-10">

        {/* ==================== HOME TAB ==================== */}
        {activeTab === 'home' && (
          <>
            {/* Welcome Card - Full bleed hero */}
            <WelcomeCard
              guestName={DEMO_GROUP.guestName}
              unitName={getLocalizedText(activeUnit.name, lang)}
              propertyName={DEMO_PROPERTY.name}
              checkIn={DEMO_GROUP.checkIn}
              checkOut={DEMO_GROUP.checkOut}
              guestsCount={activeBooking.guestsCount}
              adultsCount={activeBooking.adultsCount}
              childrenCount={activeBooking.childrenCount}
              infantsCount={activeBooking.infantsCount}
              coverImage={DEMO_IMAGES.cover}
              darkMode={darkMode}
            />

            {/* Compact Weather Banner - opens modal on click */}
            <div className="mt-6">
              <DemoWeatherBanner
                darkMode={darkMode}
                lang={lang}
                onOpen={() => setActiveModal('weather')}
              />
            </div>

            {/* Quick Actions Grid - 7 compact cards, each opens full modal */}
            <DemoQuickActionsGrid
              darkMode={darkMode}
              lang={lang}
              forceOpenModal={activeModal}
              onModalChange={(m) => setActiveModal(m)}
            />

            {/* Rest of home content with padding */}
            <div className="px-4 space-y-4">

            {/* Unit Switcher */}
            <UnitSwitcher
              units={DEMO_UNIT_BOOKINGS}
              activeUnitId={activeUnitId}
              onSwitch={setActiveUnitId}
              darkMode={darkMode}
            />

            {/* Sleeping Card - compact summary */}
            <SleepingCard
              sleeping={activeSleeping}
              darkMode={darkMode}
              onViewDetails={() => setActiveTab('unit')}
            />

            {/* Host Contact */}
            <HostContactCard
              firstName={DEMO_LANDLORD.firstName}
              lastName={DEMO_LANDLORD.lastName}
              phone={DEMO_LANDLORD.phone}
              avatarUrl={DEMO_LANDLORD.avatarUrl}
              onChatClick={() => setShowChat(true)}
              darkMode={darkMode}
              isOnline={true}
              chatEnabled={true}
              responseTimeMinutes={12}
              emergencyContact={null}
            />

            </div>{/* End of padded home content */}
          </>
        )}

        {/* ==================== UNIT TAB ==================== */}
        {activeTab === 'unit' && (
          <div className="px-4 py-4 space-y-4">
            <DemoCategoryGallery
              unitKey={activeUnitId === 'demo-unit-001' ? 'unit1' : 'unit2'}
              darkMode={darkMode}
              lang={lang}
            />

            {/* Unit Description */}
            <div className={`rounded-2xl p-5 ${darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-100 shadow-xl'}`}>
              <h3 className={`font-bold text-lg mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('guest.unit.about', 'O smještaju')}
              </h3>
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                {getLocalizedText(activeUnit.description, lang)}
              </p>
            </div>

            {/* Sleeping Section */}
            <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-100 shadow-xl'}`}>
              <SleepingSection
                sleeping={activeSleeping}
                className="p-5"
                darkMode={darkMode}
              />
            </div>

            {/* Unit Amenities */}
            <AmenitiesCard amenities={activeUnit.amenities as unknown as string[]} darkMode={darkMode} />

            {/* Shared Amenities */}
            <SharedAmenitiesCard amenities={DEMO_SHARED_AMENITIES} darkMode={darkMode} />

            {/* Facilities (pool rules, BBQ usage) */}
            <FacilitiesSection facilities={DEMO_FACILITIES} darkMode={darkMode} />

            {/* Equipment & Instructions (TV, AC, Kitchen, etc.) */}
            <GuestEquipmentSection unit={DEMO_EQUIPMENT} darkMode={darkMode} />

            {/* Unit Details */}
            <div className={`rounded-2xl p-5 ${darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-100 shadow-xl'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-cyan-500 to-blue-500' : 'bg-gradient-to-br from-cyan-400 to-blue-500'} shadow-lg shadow-cyan-500/30`}>
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('guest.unit.details', 'Detalji')}
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {t('guest.unit.detailsSubtitle', 'Osnovne informacije')}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-4 rounded-xl flex items-center gap-3 ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                    <BedDouble className={`w-5 h-5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('guest.unit.bedrooms', 'Spavaće sobe')}
                    </p>
                    <p className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {activeUnit.bedrooms}
                    </p>
                  </div>
                </div>
                <div className={`p-4 rounded-xl flex items-center gap-3 ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <Bath className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('guest.unit.bathrooms', 'Kupaonice')}
                    </p>
                    <p className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {activeUnit.bathrooms}
                    </p>
                  </div>
                </div>
                <div className={`p-4 rounded-xl flex items-center gap-3 ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                    <Users className={`w-5 h-5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('guest.unit.maxGuests', 'Maks. gostiju')}
                    </p>
                    <p className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {activeUnit.maxGuests}
                    </p>
                  </div>
                </div>
                <div className={`p-4 rounded-xl flex items-center gap-3 ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <Maximize className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('guest.unit.size', 'Površina')}
                    </p>
                    <p className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {activeUnit.sizeSqm} m²
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== EXPLORE TAB ==================== */}
        {activeTab === 'explore' && (
          <div className="px-4 py-4">
            <GuestExploreTab
              recommendations={exploreRecommendations}
              darkMode={darkMode}
            />
          </div>
        )}

        {/* ==================== HOST TAB ==================== */}
        {activeTab === 'host' && (
          <div className="px-4 py-4 space-y-4">
            <DemoHostMoments updates={DEMO_HOST_UPDATES} darkMode={darkMode} portalTarget={phoneFrameRef.current} />
            <HostCornerTab
              personalRecommendations={DEMO_PERSONAL_RECOMMENDATIONS}
              services={DEMO_SERVICES}
              hostName={DEMO_LANDLORD.firstName}
              darkMode={darkMode}
            />
          </div>
        )}

        {/* ==================== INFO TAB ==================== */}
        {activeTab === 'info' && (
          <div className="px-4 py-4 space-y-4">
            <HouseRulesCard
              rules={getLocalizedText(DEMO_HOUSE_RULES, lang)}
              darkMode={darkMode}
              checkInTime={DEMO_CHECK_TIMES.checkInTime}
              checkOutTime={DEMO_CHECK_TIMES.checkOutTime}
            />

            <HostContactCard
              firstName={DEMO_LANDLORD.firstName}
              lastName={DEMO_LANDLORD.lastName}
              phone={DEMO_LANDLORD.phone}
              avatarUrl={DEMO_LANDLORD.avatarUrl}
              onChatClick={() => setShowChat(true)}
              darkMode={darkMode}
            />

            {/* Booking Info */}
            <div className={`rounded-2xl p-5 ${darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-100 shadow-xl'}`}>
              <h3 className={`font-bold text-lg mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('guest.info.bookingDetails', 'Detalji rezervacije')}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>
                    {t('guest.info.totalPrice', 'Ukupna cijena')}
                  </span>
                  <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(activeBooking.totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>
                    {t('guest.info.status', 'Status')}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-600 dark:text-green-400">
                    {t('bookings.status.confirmed', 'Potvrđeno')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>
                    {t('guest.info.source', 'Izvor')}
                  </span>
                  <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Booking.com
                  </span>
                </div>
              </div>
            </div>

            {/* Dark Mode Toggle */}
            <div className={`rounded-2xl p-5 ${darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-100 shadow-xl'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${darkMode ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gradient-to-br from-amber-400 to-orange-500'} shadow-lg`}>
                    {darkMode ? <Moon className="w-5 h-5 text-white" /> : <Sun className="w-5 h-5 text-white" />}
                  </div>
                  <div>
                    <h3 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t('guest.info.darkMode', 'Tamni način')}
                    </h3>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {darkMode ? t('guest.info.darkModeOn', 'Uključeno') : t('guest.info.darkModeOff', 'Isključeno')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${darkMode ? 'bg-cyan-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${darkMode ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      </div>
      {/* END scrollable area */}

      {/* AI Floating Assistant - replaces old chat floating button */}
      <DemoFloatingAssistant
        darkMode={darkMode}
        onOpen={() => setExplainContext('assistant')}
      />

      {/* Navigation - inline (not fixed) so it stays at bottom of flex container */}
      <nav
        className={`shrink-0 z-50 safe-area-bottom ${
          darkMode
            ? 'bg-slate-900/95 border-t border-slate-700/50'
            : 'bg-white/95 border-t border-gray-200/80'
        } backdrop-blur-xl`}
        aria-label={t('guest.nav.label', 'Guest portal navigation')}
      >
        <div className="flex items-center justify-around py-1 px-1" role="tablist">
          {[
            { id: 'home' as GuestTab, icon: Home, label: t('guest.nav.home', 'Početna') },
            { id: 'unit' as GuestTab, icon: Bed, label: t('guest.nav.unit', 'Smještaj') },
            { id: 'explore' as GuestTab, icon: Compass, label: t('guest.nav.explore', 'Istraži') },
            { id: 'host' as GuestTab, icon: Heart, label: t('guest.nav.host', 'Domaćin') },
            { id: 'info' as GuestTab, icon: Info, label: t('guest.nav.info', 'Info') },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-label={tab.label}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex flex-col items-center py-2 px-3 rounded-2xl transition-all duration-300 min-w-[60px] min-h-[52px] ${
                  isActive ? 'scale-105' : 'hover:scale-102'
                }`}
              >
                {isActive && (
                  <div className={`absolute inset-0 rounded-2xl ${
                    darkMode
                      ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20'
                      : 'bg-gradient-to-br from-cyan-50 to-blue-50'
                  }`} />
                )}
                <div className={`relative flex items-center justify-center w-10 h-10 rounded-xl mb-0.5 transition-all duration-300 ${
                  isActive
                    ? darkMode
                      ? 'bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                      : 'bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25'
                    : darkMode ? 'text-slate-400' : 'text-gray-400'
                }`}>
                  <Icon
                    className={`w-5 h-5 transition-all duration-300 ${
                      isActive ? 'text-white' : darkMode ? 'text-slate-400' : 'text-gray-500'
                    }`}
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                  {tab.id === 'host' && DEMO_HOST_UPDATES.length > 0 && !isActive && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-cyan-500 border-2 border-white dark:border-slate-900 animate-pulse" />
                  )}
                </div>
                <span className={`relative text-[10px] font-semibold tracking-wide transition-all duration-300 ${
                  isActive
                    ? darkMode ? 'text-blue-400' : 'text-blue-600'
                    : darkMode ? 'text-slate-500' : 'text-gray-500'
                }`}>
                  {tab.label}
                </span>
                {isActive && (
                  <div className={`absolute -bottom-0.5 w-1 h-1 rounded-full ${
                    darkMode ? 'bg-blue-400' : 'bg-blue-500'
                  }`} />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Chat View - modern WhatsApp-style with blue checks, typing indicator */}
      <DemoChatScreen
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        messages={DEMO_CHAT_MESSAGES}
        hostName={`${DEMO_LANDLORD.firstName} ${DEMO_LANDLORD.lastName}`}
        darkMode={darkMode}
        showTyping={showChatTyping}
      />

        </div>
        {/* END phone frame */}

        {/* Contextual explanation tooltip — outside phone frame so desktop side card is visible.
            Mobile uses fixed positioning. Closing tooltip dismisses but doesn't reset state. */}
        <DemoExplainTooltip
          context={explainContext}
          isDesktop={isDesktop}
          darkMode={darkMode}
        />
      </div>
      {/* END outer wrapper */}
    </>
  );
}
